import type {
  Participant,
  ParticipantService,
} from '@/scopes/account/group-responsible/domain/types'
import type { GroupBookingAPIResponse } from '@/scopes/account/group-responsible/infrastructure/types'
import {
  getAmountDue,
  getParticipantName,
  getParticipantPaymentStatus,
} from './utils'

type Service = GroupBookingAPIResponse['data']['childBookings'][number]['services'][number]

function getServiceForParticipant(
  services: Service[],
  typeOfService: string,
  participantId: number,
): ParticipantService | undefined {
  const service = services.find(service =>
    service.typeOfService === typeOfService
    && service.participantIds.includes(participantId),
  )
  return mapService(service, participantId)
}

function getServiceByGroupCode(
  services: Service[],
  participantId: number,
  groupCode: string,
): ParticipantService | undefined {
  const target = groupCode.toUpperCase()
  const service = services.find((s) => {
    if (!s.participantIds.includes(participantId))
      return false
    const code = (s.groupCode ?? '').toUpperCase()
    return code.includes(target)
  })
  return mapService(service, participantId)
}

function mapService(
  service: Service | undefined,
  participantId: number,
): ParticipantService | undefined {
  if (!service)
    return undefined
  const price = service.prices?.find(p => p.participantsIds.includes(participantId))?.unitPrice.amount ?? 0
  return {
    description: service.typeDescription ?? '',
    price,
  }
}

export function mapParticipants(
  childBookings: GroupBookingAPIResponse['data']['childBookings'],
): Record<string, Participant> {
  return childBookings
    .flatMap((item) => {
      const allParticipants = [...item.participants, ...item.cancelledParticipants]
      return allParticipants.map((participant) => {
        const skiPass = getServiceForParticipant(item.services, 'SkiPass', participant.participantId)
        const skiMaterial = getServiceForParticipant(item.services, 'SkiMaterial', participant.participantId)
        const sheets = getServiceByGroupCode(item.services, participant.participantId, 'SHEET')
        const towels = getServiceByGroupCode(item.services, participant.participantId, 'TOWEL')
        const travelInsurance = getServiceByGroupCode(item.services, participant.participantId, 'TRAVELIN')
        const cancelInsurance = getServiceByGroupCode(item.services, participant.participantId, 'CANCELIN')
        const policyCosts = getServiceByGroupCode(item.services, participant.participantId, 'POLICY')
        const sweaterSize = getServiceByGroupCode(item.services, participant.participantId, 'SWEATER')
        const skiLesson = getServiceByGroupCode(item.services, participant.participantId, 'SKIL')
        const earlyDeparture = getServiceByGroupCode(item.services, participant.participantId, 'EARLYDEPARTUREDK')

        const isCancelled = participant.status?.toLowerCase() === 'cancelled'
        const hasRoomAssigned = !isCancelled && participant.used

        return {
          id: participant.participantId.toString(),
          bookingNumber: item.bookingNumber,
          bookingCustomerId: participant.bookingCustomerId,
          name: getParticipantName(participant.firstName, participant.lastName),
          email: participant.email,
          paymentStatus: getParticipantPaymentStatus(participant.paymentInfo),
          isCancelled,
          hasRoomAssigned,
          isRegistered: participant.used,
          amountDue: getAmountDue(participant.paymentInfo),
          telephoneNumber: participant.telephoneNumber,
          mobileNumber: participant.mobileNumber,
          skiPass,
          skiMaterial,
          sheets,
          towels,
          travelInsurance,
          cancelInsurance,
          policyCosts,
          sweaterSize,
          skiLesson,
          earlyDeparture,
          isBusAssignmentProcessing: participant.isBusAssignmentProcessing || false,
        }
      })
    })
    .reduce((acc: Record<string, Participant>, participant) => {
      acc[participant.id] = participant
      return acc
    }, {})
}
